import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Sun, Moon, Mail, Lock, ArrowLeft, Eye, EyeOff, UserRoundCog, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AppVersion } from '@/components/AppVersion'
import { EnvironmentCard } from '@/components/EnvironmentCard'
import { AuthService, isTwoFactorChallenge } from '@/services/core/auth'
import { StorageService } from '@/services/core/storage'
import { getUserNetwork } from '@/services/core/context'
import { WebAuthnService, PasskeyCancelledError } from '@/services/core/webauthn'
import { UserApi } from '@/services/users/userApi'
import { NetworksApi } from '@/services/networks/networksApi'
import { useTheme } from '../contexts/ThemeContext'

import '../styles/login.css'

function BrandPanel() {
  const { t } = useTranslation()
  return (
    <aside className="auth-brand">
      <div className="auth-brand__topline">
        <img src="/logo.svg" alt="" className="auth-brand__logo" />
        <span>ADNUNTIUS</span>
      </div>

      <div className="relative flex flex-1 items-center justify-center py-12">
        <img src="/logo.svg" alt="" className="size-48 xl:size-64" />
      </div>

      <div className="auth-brand__copy">
        <div className="auth-brand__eyebrow">{t('brand.eyebrow')}</div>
        <h1 className="auth-brand__title">
          {t('brand.heading')}
        </h1>
        <p className="auth-brand__body">
          {t('brand.body')}
        </p>

        <div className="auth-brand__stats">
          <div>
            <div className="auth-brand__stat-value">4</div>
            <div className="auth-brand__stat-label">{t('brand.statMarkets')}</div>
          </div>
          <div>
            <div className="auth-brand__stat-value">99.98%</div>
            <div className="auth-brand__stat-label">{t('brand.statUptime')}</div>
          </div>
          <div>
            <div className="auth-brand__stat-value">2.4T</div>
            <div className="auth-brand__stat-label">{t('brand.statImpressions')}</div>
          </div>
        </div>
      </div>

      <div className="auth-brand__footer">
        <span>{t('brand.copyright')}</span>
        <span>
          <a href="https://docs.adnuntius.com">{t('brand.docs')}</a> ·{' '}
          <a href="https://docs.adnuntius.com/troubleshooting/how-do-i-contact-support">{t('brand.support')}</a>
        </span>
      </div>
    </aside>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [twoFa, setTwoFa] = useState<{ authToken: string; type: string } | null>(null)
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', ''])
  const otpValue = otpDigits.join('')
  const otpInputRef = useRef<HTMLInputElement | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const masqueradeEnabled = StorageService.isMasqueradeEnabled()
  // A masquerade link from the users list names who to sign in as and, when
  // known, the support user to sign in with.
  const prefill = useRef(new URLSearchParams(location.search)).current
  const prefillTarget = masqueradeEnabled ? (prefill.get('masqUsername') ?? '') : ''
  const [masqueradeOpen, setMasqueradeOpen] = useState(!!prefillTarget)
  const [masqTargetUsername, setMasqTargetUsername] = useState(prefillTarget)
  const [masqSubmitting, setMasqSubmitting] = useState(false)
  const pendingMasqueradeTarget = useRef<string | null>(null)
  const initialUsername = useRef(
    prefill.get('supportUsername') ||
      StorageService.getLastLoginUsername() ||
      StorageService.getCurrentUser()?.username ||
      ''
  ).current
  const credentialsRef = useRef({ username: initialUsername, password: '' })
  const passkeySupported = WebAuthnService.isSupported()
  const [passkeySubmitting, setPasskeySubmitting] = useState(false)

  // Reaching the login screen ends any session, masqueraded or not — so a
  // masquerade left over from a previous one must not warn about this one.
  useEffect(() => { StorageService.setMasquerade(null) }, [])

  // The prefill is read once; it does not stay in the address bar.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has('masqUsername') && !params.has('supportUsername')) return
    params.delete('masqUsername')
    params.delete('supportUsername')
    navigate({ search: params.toString() }, { replace: true, state: location.state })
  }, [location.search, location.state, navigate])

  const loginForm = useForm({
    defaultValues: { username: initialUsername, password: '' },
  })

  const navigateAfterLogin = async (knownUsername: string, loginUsername = knownUsername) => {
    // UII starts independent bootstrap requests in the same wave. Fetch the
    // user and network list together so the destination does not serialize them.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'currentUser' })
    const [apiUser, networks] = await Promise.all([
      UserApi.getCurrent(),
      queryClient.fetchQuery({
        queryKey: ['networks'],
        queryFn: NetworksApi.getAll,
        staleTime: Infinity,
      }),
    ])

    // Passkey sign-in can succeed without a typed username — the API user is the source of truth then.
    const username = knownUsername || apiUser.username || ''
    const usernameToRemember =
      loginUsername || (StorageService.getMasquerade() ? '' : apiUser.username)
    if (usernameToRemember) StorageService.setLastLoginUsername(usernameToRemember)
    const accessibleNetworks = networks.filter((network) =>
      Boolean(apiUser.permissionsByNetwork[network.tagId])
    )
    const savedTagId = localStorage.getItem('adnuntius.network')
    const userTagId = getUserNetwork(apiUser.id)
    const currentNetwork =
      accessibleNetworks.find((network) => network.tagId === userTagId) ??
      accessibleNetworks.find((network) => network.tagId === savedTagId) ??
      accessibleNetworks[0]

    if (currentNetwork) {
      if (currentNetwork.tagId !== savedTagId) {
        localStorage.setItem('adnuntius.network', currentNetwork.tagId)
      }
      void queryClient.prefetchQuery({
        queryKey: ['network', currentNetwork.tagId],
        queryFn: () => NetworksApi.getByTagId(currentNetwork.tagId),
        staleTime: Infinity,
      })
    }

    queryClient.setQueryData(['currentUser'], apiUser)
    StorageService.setCurrentUser({
      id: `api-${btoa(username)}`,
      username: apiUser.username || username,
      displayName: apiUser.displayName || apiUser.name || username.split('@')[0],
    })

    const returnTo = location.state?.returnTo
    if (typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//') && returnTo !== '/login') {
      navigate(returnTo, { replace: true })
      return
    }

    const storedModule = localStorage.getItem('adnuntius.workspace.module')
    if (storedModule) {
      const LANDING: Record<string, string> = {
        'advertising': '/advertising',
        publishing: '/publishing',
        political: '/political',
      }
      navigate(LANDING[storedModule] || '/select-module')
    } else {
      navigate('/select-module')
    }
  }

  const handleLogin = async () => {
    const username = credentialsRef.current.username
    const password = credentialsRef.current.password
    if (!username || !password) {
      // Name only what is actually missing.
      const detail = !username
        ? !password
          ? t('login.missingFieldsDetail')
          : t('login.missingEmail')
        : t('login.missingPassword')
      toast.warning(t('login.missingFieldsTitle'), { description: detail, duration: 4000 })
      return
    }
    setSubmitting(true)
    try {
      const result = await AuthService.login(username, password)
      if (isTwoFactorChallenge(result)) {
        setTwoFa({ authToken: result.authToken, type: result.type })
        setSubmitting(false)
        return
      }
      await navigateAfterLogin(username)
    } catch (err) {
      toast.error(t('login.signInFailed'), {
        description: err instanceof Error ? err.message : t('login.authFailed'),
        duration: 5000,
      })
      setSubmitting(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!twoFa || !otpValue) return
    const code = String(otpValue)
    if (code.length !== 6) {
      toast.warning(t('login.invalidCode'), {
        description: t('login.invalidCodeDetail'),
        duration: 4000,
      })
      return
    }
    setSubmitting(true)
    try {
      await AuthService.verify2fa(twoFa.authToken, code)
      const masqTarget = pendingMasqueradeTarget.current
      if (masqTarget) {
        StorageService.setMasquerade({
          targetUsername: masqTarget,
          originalToken: 'null',
          supportUsername: credentialsRef.current.username || undefined,
        })
        pendingMasqueradeTarget.current = null
        toast.success(t('masquerade.success', { username: masqTarget }))
        await navigateAfterLogin(masqTarget, credentialsRef.current.username)
      } else {
        await navigateAfterLogin(credentialsRef.current.username)
      }
    } catch (err) {
      toast.error(t('login.verificationFailed'), {
        description: err instanceof Error ? err.message : t('login.twoFactorFailed'),
        duration: 5000,
      })
      setOtpDigits(['', '', '', '', '', ''])
      setSubmitting(false)
    }
  }


  const handleMasquerade = async () => {
    const adminUsername = credentialsRef.current.username
    const password = credentialsRef.current.password
    if (!adminUsername || !password || !masqTargetUsername) {
      toast.warning(t('masquerade.missingFields'))
      return
    }
    setMasqSubmitting(true)
    const originalToken = 'null'
    try {
      const result = await AuthService.masquerade(adminUsername, password, masqTargetUsername)
      if (isTwoFactorChallenge(result)) {
        pendingMasqueradeTarget.current = masqTargetUsername
        setTwoFa({ authToken: result.authToken, type: result.type })
        setMasqSubmitting(false)
        return
      }
      StorageService.setMasquerade({
        targetUsername: masqTargetUsername,
        originalToken,
        supportUsername: adminUsername,
      })
      toast.success(t('masquerade.success', { username: masqTargetUsername }))
      await navigateAfterLogin(masqTargetUsername, adminUsername)
    } catch (err) {
      toast.error(t('masquerade.failed'), {
        description: err instanceof Error ? err.message : undefined,
      })
      setMasqSubmitting(false)
    }
  }

  const handlePasskeyLogin = async () => {
    const username = credentialsRef.current.username
    const masqTarget = masqueradeArmed ? masqTargetUsername : undefined
    setPasskeySubmitting(true)
    try {
      await WebAuthnService.login(username, masqTarget)
      if (masqTarget) {
        StorageService.setMasquerade({
          targetUsername: masqTarget,
          originalToken: 'null',
          supportUsername: username || undefined,
        })
        toast.success(t('masquerade.success', { username: masqTarget }))
        await navigateAfterLogin(masqTarget, username)
      } else {
        await navigateAfterLogin(username)
      }
    } catch (err) {
      setPasskeySubmitting(false)
      if (err instanceof PasskeyCancelledError) return
      toast.error(t('login.passkeyFailed'), {
        description: err instanceof Error ? err.message : undefined,
        duration: 5000,
      })
    }
  }

  // The masquerade target lives in the same form as the credentials, so submitting has to
  // route to the masquerade grant whenever a target is armed — otherwise a plain
  // /authenticate goes out and masqueradeAsUser is silently dropped.
  const masqueradeArmed = masqueradeEnabled && masqueradeOpen && !!masqTargetUsername

  const handleSubmit = () => (masqueradeArmed ? handleMasquerade() : handleLogin())

  return (
    <div className="auth-shell">
      <BrandPanel />

      <div className="auth-form-wrap">
        <button
          type="button"
          className="auth-theme-toggle"
          onClick={toggleTheme}
          title={theme === 'dark' ? t('login.switchToLight') : t('login.switchToDark')}
        >
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        <div className="auth-form">
          <EnvironmentCard className="mb-6" />
          {!twoFa ? (
            <>
              <div className="auth-form__eyebrow">{t('login.eyebrow')}</div>
              <h2 className="auth-form__title">{t('login.heading')}</h2>
              <p className="auth-form__lede">{t('login.lede')}</p>

              <form className="auth-form__stack" onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
                <div className="auth-field">
                  <label htmlFor="username">{t('login.emailLabel')}</label>
                  <loginForm.Field name="username">
                    {(field) => (
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--ad-text-muted)" />
                        <Input
                          id="username"
                          name="username"
                          type="email"
                          placeholder={t('login.emailPlaceholder')}
                          value={field.state.value}
                          onChange={(e) => { field.handleChange(e.target.value); credentialsRef.current.username = e.target.value }}
                          autoComplete="username"
                          autoFocus={!initialUsername}
                          className="pl-9"
                        />
                      </div>
                    )}
                  </loginForm.Field>
                </div>

                <div className="auth-field">
                  <label htmlFor="password">{t('login.passwordLabel')}</label>
                  <loginForm.Field name="password">
                    {(field) => (
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--ad-text-muted)" />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={field.state.value}
                          onChange={(e) => { field.handleChange(e.target.value); credentialsRef.current.password = e.target.value }}
                          autoComplete="current-password"
                          autoFocus={!!initialUsername}
                          className="pl-9 pr-9"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-(--ad-text-muted) hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </button>
                      </div>
                    )}
                  </loginForm.Field>
                </div>

                {masqueradeEnabled && (
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-sm text-(--ad-text-muted) hover:text-foreground transition-colors w-fit"
                      onClick={() => setMasqueradeOpen((v) => !v)}
                    >
                      <UserRoundCog className="size-4" />
                      {t('masquerade.loginToggle')}
                    </button>

                    <div
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{ maxHeight: masqueradeOpen ? '120px' : '0', opacity: masqueradeOpen ? 1 : 0 }}
                    >
                      <div className="auth-field">
                        <label htmlFor="masq-target">{t('masquerade.targetLabel')}</label>
                        <div className="relative">
                          <UserRoundCog className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--ad-text-muted)" />
                          <Input
                            id="masq-target"
                            type="email"
                            placeholder={t('masquerade.targetPlaceholder')}
                            value={masqTargetUsername}
                            onChange={(e) => setMasqTargetUsername(e.target.value)}
                            className="pl-9"
                            tabIndex={masqueradeOpen ? 0 : -1}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || masqSubmitting || passkeySubmitting}
                  loading={submitting || masqSubmitting}
                >
                  {masqueradeArmed
                    ? (masqSubmitting ? t('masquerade.starting') : t('masquerade.start'))
                    : (submitting ? t('login.signingIn') : t('login.signIn'))}
                </Button>

                {passkeySupported && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handlePasskeyLogin}
                    disabled={submitting || masqSubmitting || passkeySubmitting}
                    loading={passkeySubmitting}
                  >
                    {!passkeySubmitting && <KeyRound className="size-4" />}
                    {masqueradeArmed ? t('login.masqueradeWithPasskey') : t('login.signInWithPasskey')}
                  </Button>
                )}
              </form>
            </>
          ) : (
            <>
              <div className="auth-form__eyebrow">{t('login.twoFactorEyebrow')}</div>
              <h2 className="auth-form__title">{t('login.twoFactorHeading')}</h2>
              <p className="auth-form__lede">
                {twoFa.type === 'EMAIL'
                  ? t('login.twoFactorLedeEmail')
                  : t('login.twoFactorLede')}
              </p>

              <form className="auth-form__stack" onSubmit={(e) => { e.preventDefault(); handleVerifyOtp() }}>
                <div className="relative flex justify-center" onClick={() => otpInputRef.current?.focus()}>
                  {/* Visual digit boxes */}
                  <div className="flex gap-2 pointer-events-none select-none" aria-hidden>
                    {otpDigits.map((digit, i) => (
                      <div
                        key={i}
                        className="w-10 h-10 flex items-center justify-center border rounded-(--ad-radius-sm) text-lg font-mono border-(--ad-border) bg-(--ad-surface)"
                      >
                        {digit}
                      </div>
                    ))}
                  </div>
                  {/* Single real input — transparent text so Bitwarden can fill it */}
                  <input
                    ref={otpInputRef}
                    id="otp"
                    name="otp"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={otpValue}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setOtpDigits(Array.from({ length: 6 }, (_, i) => val[i] ?? ''))
                    }}
                    onPaste={(e) => {
                      e.preventDefault()
                      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
                      setOtpDigits(Array.from({ length: 6 }, (_, i) => pasted[i] ?? ''))
                    }}
                    className="absolute inset-0 w-full h-full bg-transparent border-0 outline-none cursor-text"
                    style={{ color: 'transparent', caretColor: 'transparent' }}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || otpValue.length !== 6}
                  loading={submitting}
                >
                  {submitting ? t('login.verifying') : t('login.verify')}
                </Button>

                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => { setTwoFa(null); setOtpDigits(['', '', '', '', '', '']) }}
                >
                  <ArrowLeft className="size-4" /> {t('login.backToSignIn')}
                </Button>
              </form>
            </>
          )}
        </div>
        <AppVersion className="absolute right-6 bottom-4" />
      </div>
    </div>
  )
}
